import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, Navigate, Outlet, ScrollRestoration, useLocation } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { AppShell } from './components/AppShell';
import { PublicHeader } from './components/PublicHeader';
import { SiteFooter } from './components/SiteFooter';
import { ErrorState, PageLoader } from './components/States';
import { ToastProvider } from './components/Toasts';
import { HomePage } from './pages/HomePage';
import { LandingPage } from './pages/LandingPage';
import { NotFoundPage, RouteError } from './pages/NotFoundPage';
import { usePageMeta } from './hooks/usePageMeta';
import { usePremiumExpiry } from './hooks/usePremiumExpiry';
import { usePersonalPalette } from './hooks/usePersonalPalette';
import { ApiError } from './services/api';
import { keys, useSession } from './services/queries';
import { setAmbientBed } from './services/ambient';
import type { LegalDocumentId } from './pages/LegalPage';

const AnimeDetailPage = lazy(() => import('./features/collection/AnimeDetailPage').then((m) => ({ default: m.AnimeDetailPage })));
const AnimesPage = lazy(() => import('./features/collection/AnimesPage').then((m) => ({ default: m.AnimesPage })));
const CollectionPage = lazy(() => import('./features/collection/CollectionPage').then((m) => ({ default: m.CollectionPage })));
const FragmentsPage = lazy(() => import('./features/collection/FragmentsPage').then((m) => ({ default: m.FragmentsPage })));
const PurgePage = lazy(() => import('./features/collection/PurgePage').then((m) => ({ default: m.PurgePage })));
const BoostersPage = lazy(() => import('./features/gacha/BoostersPage').then((m) => ({ default: m.BoostersPage })));
const ProfilePage = lazy(() => import('./features/profile/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const PublicProfilePage = lazy(() => import('./features/profile/PublicProfilePage').then((m) => ({ default: m.PublicProfilePage })));
const QuestsPage = lazy(() => import('./features/quests/QuestsPage').then((m) => ({ default: m.QuestsPage })));
const HarvestPage = lazy(() => import('./features/harvest/HarvestPage').then((m) => ({ default: m.HarvestPage })));
const LoginCalendarPage = lazy(() =>
  import('./features/login-calendar/LoginCalendarPage').then((m) => ({ default: m.LoginCalendarPage })),
);
const FriendsPage = lazy(() => import('./features/social/FriendsPage').then((m) => ({ default: m.FriendsPage })));
const AtlasPage = lazy(() => import('./features/social/AtlasPage').then((m) => ({ default: m.AtlasPage })));
const ReferralPage = lazy(() => import('./features/social/ReferralPage').then((m) => ({ default: m.ReferralPage })));
const InboxPage = lazy(() => import('./features/messaging/InboxPage').then((m) => ({ default: m.InboxPage })));
const ConversationPage = lazy(() => import('./features/messaging/ConversationPage').then((m) => ({ default: m.ConversationPage })));
const NotificationsPage = lazy(() => import('./features/notifications/NotificationsPage').then((m) => ({ default: m.NotificationsPage })));
const MarketLayout = lazy(() => import('./features/marketplace/MarketPage').then((m) => ({ default: m.MarketLayout })));
const MarketListingsPage = lazy(() => import('./features/marketplace/MarketPage').then((m) => ({ default: m.MarketListingsPage })));
const MarketAuctionsPage = lazy(() => import('./features/marketplace/MarketPage').then((m) => ({ default: m.MarketAuctionsPage })));
const MarketMinePage = lazy(() => import('./features/marketplace/MarketPage').then((m) => ({ default: m.MarketMinePage })));
const MarketMyAuctionsPage = lazy(() => import('./features/marketplace/MarketPage').then((m) => ({ default: m.MarketMyAuctionsPage })));
const MarketHistoryPage = lazy(() => import('./features/marketplace/MarketPage').then((m) => ({ default: m.MarketHistoryPage })));
const AuctionDetailPage = lazy(() => import('./features/auctions/AuctionDetailPage').then((m) => ({ default: m.AuctionDetailPage })));
const TradesPage = lazy(() => import('./features/trades/TradesPage').then((m) => ({ default: m.TradesPage })));
const TradeDetailPage = lazy(() => import('./features/trades/TradesPage').then((m) => ({ default: m.TradeDetailPage })));
const TradeBuilderPage = lazy(() => import('./features/trades/TradesPage').then((m) => ({ default: m.TradeBuilderPage })));
const LoginPage = lazy(() => import('./pages/AuthPages').then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('./pages/AuthPages').then((m) => ({ default: m.RegisterPage })));
const RulesPage = lazy(() => import('./pages/RulesPage').then((m) => ({ default: m.RulesPage })));
const LegalDocument = lazy(() => import('./pages/LegalPage').then((m) => ({ default: m.LegalDocument })));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage })));
const PublicCharacterPage = lazy(() => import('./pages/PublicCharacterPage').then((m) => ({ default: m.PublicCharacterPage })));
const PublicAnimePage = lazy(() => import('./pages/PublicAnimePage').then((m) => ({ default: m.PublicAnimePage })));
const AdminShell = lazy(() => import('./features/staff/AdminShell').then((m) => ({ default: m.AdminShell })));
const PremiumPage = lazy(() => import('./features/premium/PremiumPage').then((m) => ({ default: m.PremiumPage })));

function onUnauthorized(error: unknown) {
  if (error instanceof ApiError && error.status === 401) queryClient.setQueryData(keys.session, { user: null, staff: null });
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onUnauthorized }),
  mutationCache: new MutationCache({ onError: onUnauthorized }),
  defaultOptions: {
    queries: {
      staleTime: 20_000,
      refetchOnWindowFocus: false,
      retry: (failures, error) => !(error instanceof ApiError && error.status >= 400 && error.status < 500) && failures < 2,
    },
  },
});

function Root() {
  usePageMeta();
  usePremiumExpiry();
  usePersonalPalette();
  const { pathname } = useLocation();
  useEffect(() => {
    setAmbientBed(pathname.startsWith('/moisson') ? 'harvest' : 'night');
  }, [pathname]);
  return (
    <>
      <ScrollRestoration />
      <Suspense fallback={<PageLoader />}>
        <Outlet />
      </Suspense>
    </>
  );
}

function HomeRoute() {
  const session = useSession();
  if (session.isPending) return <PageLoader />;
  return session.data?.user ? (
    <AppShell>
      <HomePage />
    </AppShell>
  ) : (
    <LandingPage />
  );
}

function GuestFrame({ children }: { children: ReactNode }) {
  return (
    <>
      <PublicHeader />
      <main className="public-page" id="contenu" tabIndex={-1}>
        {children}
        <SiteFooter />
      </main>
    </>
  );
}

function WithShell({ children }: { children: ReactNode }) {
  const session = useSession();
  if (session.isPending) return <PageLoader />;
  return session.data?.user ? <AppShell>{children}</AppShell> : <GuestFrame>{children}</GuestFrame>;
}

function RulesRoute() {
  return (
    <WithShell>
      <RulesPage />
    </WithShell>
  );
}

function LegalRoute({ id }: { id: LegalDocumentId }) {
  return (
    <WithShell>
      <LegalDocument id={id} />
    </WithShell>
  );
}

function PlayerRoute() {
  const session = useSession();
  if (session.isPending) return <PageLoader />;
  return session.data?.user ? (
    <AppShell>
      <PublicProfilePage />
    </AppShell>
  ) : (
    <>
      <PublicHeader />
      <main className="public-page" id="contenu" tabIndex={-1}>
        <PublicProfilePage />
        <SiteFooter />
      </main>
    </>
  );
}

function PublicShell({ children }: { children: ReactNode }) {
  const session = useSession();
  if (session.isPending) return <PageLoader />;
  return session.data?.user ? (
    <AppShell>{children}</AppShell>
  ) : (
    <>
      <PublicHeader />
      <main className="public-page" id="contenu" tabIndex={-1}>
        {children}
        <SiteFooter />
      </main>
    </>
  );
}

function CharacterRoute() {
  return (
    <PublicShell>
      <PublicCharacterPage />
    </PublicShell>
  );
}

function AnimeDetailRoute() {
  const session = useSession();
  if (session.isPending) return <PageLoader />;
  if (session.data?.user) {
    return (
      <AppShell>
        <AnimeDetailPage />
      </AppShell>
    );
  }
  return (
    <>
      <PublicHeader />
      <main className="public-page" id="contenu" tabIndex={-1}>
        <PublicAnimePage />
        <SiteFooter />
      </main>
    </>
  );
}

function AdminGate() {
  const session = useSession();
  const location = useLocation();
  if (session.isPending) return <PageLoader />;
  if (!session.data?.user) {
    return <Navigate to={`/connexion?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }
  if (!session.data.staff) return <NotFoundPage />;
  return (
    <Suspense fallback={<PageLoader />}>
      <AdminShell />
    </Suspense>
  );
}

function RequireAuth() {
  const session = useSession();
  const location = useLocation();
  if (session.isPending) return <PageLoader />;
  if (session.isError) return <ErrorState error={session.error} onRetry={() => void session.refetch()} />;
  if (!session.data?.user) {
    return <Navigate to={`/connexion?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  return <AppShell />;
}

const router = createBrowserRouter([
  {
    element: <Root />,
    errorElement: <RouteError />,
    children: [
      { path: '/', element: <HomeRoute /> },
      { path: '/connexion', element: <LoginPage /> },
      { path: '/inscription', element: <RegisterPage /> },
      { path: '/verifier-email', element: <VerifyEmailPage /> },
      { path: '/regles', element: <RulesRoute /> },
      { path: '/premium', element: <WithShell><PremiumPage /></WithShell> },
      { path: '/conditions', element: <LegalRoute id="terms" /> },
      { path: '/conditions-vente', element: <LegalRoute id="sales" /> },
      { path: '/confidentialite', element: <LegalRoute id="privacy" /> },
      { path: '/mentions-legales', element: <LegalRoute id="notice" /> },
      { path: '/cookies', element: <LegalRoute id="cookies" /> },
      { path: '/joueurs/:username', element: <PlayerRoute /> },
      { path: '/personnages/:characterId', element: <CharacterRoute /> },
      { path: '/animes/:animeId', element: <AnimeDetailRoute /> },
      { path: '/admin/*', element: <AdminGate /> },
      {
        element: <RequireAuth />,
        children: [
          { path: '/boosters', element: <BoostersPage /> },
          { path: '/collection', element: <CollectionPage /> },
          { path: '/fragments', element: <FragmentsPage /> },
          { path: '/purge', element: <PurgePage /> },
          { path: '/animes', element: <AnimesPage /> },
          { path: '/quetes', element: <QuestsPage /> },
          { path: '/calendrier', element: <LoginCalendarPage /> },
          { path: '/moisson', element: <HarvestPage /> },
          { path: '/amis', element: <FriendsPage /> },
          { path: '/amis/atlas', element: <AtlasPage /> },
          { path: '/amis/parrainage', element: <ReferralPage /> },
          { path: '/messages', element: <InboxPage /> },
          { path: '/messages/:conversationId', element: <ConversationPage /> },
          { path: '/notifications', element: <NotificationsPage /> },
          { path: '/profil', element: <ProfilePage /> },
          {
            element: <MarketLayout />,
            children: [
              { path: '/marche', element: <MarketListingsPage /> },
              { path: '/marche/encheres', element: <MarketAuctionsPage /> },
              { path: '/marche/mes-ventes', element: <MarketMinePage /> },
              { path: '/marche/mes-encheres', element: <MarketMyAuctionsPage /> },
              { path: '/marche/historique', element: <MarketHistoryPage /> },
              { path: '/echanges', element: <TradesPage /> },
            ],
          },
          { path: '/marche/encheres/:auctionId', element: <AuctionDetailPage /> },
          { path: '/echanges/nouveau/:username', element: <TradeBuilderPage /> },
          { path: '/echanges/:tradeId', element: <TradeDetailPage /> },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </QueryClientProvider>
  );
}
